import React, { createContext, useContext, useState, useEffect } from "react";

interface UserSettings {
  textSize: number;
  volume: number;
  showMicButton: boolean;
}

interface SettingsContextType {
  settings: UserSettings;
  updateSettings: (newSettings: Partial<UserSettings>) => void;
}

const SettingsContext = createContext<SettingsContextType | null>(null);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

interface SettingsProviderProps {
  children: React.ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
}) => {
  const [settings, setSettings] = useState<UserSettings>({
    textSize: 16,
    volume: 50,
    showMicButton: false,
  });

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedSettings = localStorage.getItem("userSettings");
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSettings({
          textSize: parsed.textSize ?? 16,
          volume: 50, // Always use default volume, don't load from localStorage
          showMicButton: parsed.showMicButton ?? false,
        });
      } catch (error) {
        console.error("Error parsing saved settings:", error);
      }
    }
  }, []);

  // Apply text size to document root when settings change
  useEffect(() => {
    document.documentElement.style.fontSize = `${settings.textSize}px`;
  }, [settings.textSize]);

  const updateSettings = (newSettings: Partial<UserSettings>) => {
    const updatedSettings = { ...settings, ...newSettings };
    setSettings(updatedSettings);
    // Only save textSize to localStorage, not volume
    localStorage.setItem(
      "userSettings",
      JSON.stringify({
        textSize: updatedSettings.textSize,
        showMicButton: updatedSettings.showMicButton,
      })
    );
  };

  const value: SettingsContextType = {
    settings,
    updateSettings,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
